import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { type CoMingleReturnProps, groupReturnRowKeys, toReturnShipper, withCoMingleReturns } from '@/pages/return/coMingle'
import { flushSync } from 'react-dom'
import dayjs from 'dayjs'
import { failureError, invalidError, notice, rowInvalidError } from '@/components/pbo/notice'
import { FETCH_ALL_MESSAGES, guardFetching, noticeIfUploadExceedsPage } from '@/hooks/useFetchAllRows'
import { callToAddImportLog } from '@/services/excel/apis'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, type ShipperScope, scopedShipperCode, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MediaModal } from '@/components/pbo/MediaModal'
import { buildShipperNameOptions } from '@/components/system/shipperSelectFilter'
import { useGetShipperOptions } from '@/services/shipper/useShipperQueries'
import { excludeAllOption, toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { useRowMediaFiles } from '@/services/commonFile/useRowMediaFiles'
import { downloadReturnManagements } from '@/services/return/returnManagement/apis'
import { GetReturnManagementReq, OrderSearchItemRes, OrderSearchReq, ReturnManagementRow } from '@/services/return/returnManagement/types'
import {
  useConfirmReturnManagements,
  useGetReturnManagements,
  useSaveReturnManagements,
  useSearchReturnOrders,
} from '@/services/return/returnManagement/useReturnManagementQueries'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ReturnManagePageView, { type ActionCtx, type CellTarget, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { parseReturnManagementExcel } from './components/excelImport'
import { BE_REQUIRED_FIELDS, type RequiredField, useReturnManagementRows } from './hooks/useReturnManagementRows'
import {
  BLOB_REVOKE_DELAY_MS,
  buildDefaultFilters,
  DEFAULT_PAGE_SIZE,
  MESSAGES,
  ORDER_SEARCH_DATE_FORMAT,
  ORDER_SEARCH_DAYS,
  ReturnManagementFormValues,
} from './constants'
import { hasRequiredFilter, toReturnManagementReq } from './filterToRequest'
import { ReturnManagementSearchQuery, fromSearchQuery, toSearchQuery } from './searchQuery'
import { downloadReasonSchema } from './schemas/downloadReasonSchema'

/**
 * RGR04 반품관리 — 화면(겉)은 컨덕터 생성본(`handoff/page.generated.tsx`)이고 이 파일은 배선이다(컴포넌트 모드, 컨덕터 문서 51).
 *
 * 옛 손코드 화면(`ReturnManagementPage` + `components/`)의 훅·API·검증을 그대로 슬롯에 잇는다. 편집 행 상태는 옛 훅
 * `useReturnManagementRows`(카멜케이스 행)가 계속 들고, 뷰에는 컬럼 키(UPPER_SNAKE)로 바꿔 넘긴다(toViewRow ↔ fromViewRow).
 *
 * 데모(정본)를 따라 옛 화면과 달라진 동작 — PR 본문에 적는다:
 *  · [+ 추가]는 우측 끝, 새 행은 **맨 위**(PD 확정 2026-09-30 — 코드관리 [+ 추가]와 같다). 엑셀 업로드 행도 맨 위.
 *  · [저장]은 **고른 행**만(데모: 선택이 있어야 눌린다 — 상품관리 ma02 와 같은 규약). 고르지 않은 편집은 저장 뒤 재조회에도 남는다.
 *  · [삭제]는 확인창에서 **즉시** 서버 반영(옛 화면은 다음 [저장]까지 미뤘다 — 데모 확인창·"삭제되었습니다." 토스트가 즉시 삭제다).
 *  · 페이지 크기 10,000(MOMS 공통) · 주문검색은 모달 [조회]를 눌러야 후보가 나온다(옛 화면은 열자마자 조회).
 *
 * 첨부·동영상은 옛 `MediaModal`(서버 업로드)을 그대로 쓴다 — 뷰 미디어 모달(`core.MediaManager`)은 파일 이름뿐이라
 * 업로드를 못 잇는다(가이드 52 §5-A ⑮). `cellOpens` 가 false 를 돌려 뷰 모달 대신 이 모달을 연다.
 */

// ── 행 키 변환(가이드 52 §5-A ①④) ──────────────────────────────────────────────

/** 뷰 컬럼 키 ↔ 편집 행 필드 — **편집 셀만**. 표시 전용 칸(화주코드·SKUID·개수·생성/수정)은 되돌리지 않는다(규칙 4). */
const EDITABLE_FIELDS = {
  ORD_NO: 'ordNo',
  FLAG: 'flag',
  PLN_DATE: 'plnDate',
  TRS_INVOICE_NO: 'trsInvoiceNo',
  ORD_OPT_NO: 'ordOptNo',
  RL_CHANNEL: 'rlChannel',
  REF_NO2: 'refNo2',
  REF_NO3: 'refNo3',
  BARCODE: 'barcode',
  PROC_NM: 'procNm',
  REF_NO1: 'refNo1',
  GDS_CD: 'gdsCd',
  GDS_NM: 'gdsNm',
  GDS_OPT: 'gdsOpt',
  ORD_QTY: 'ordQty',
  TRS_NM: 'trsNm',
  TRS_PHONE: 'trsPhone',
  TRS_NUMBER: 'trsNumber',
  TRS_ADDR: 'trsAddr',
  SAL_CHANNEL: 'salChannel',
  BRD_NM: 'brdNm',
  TRS_LSP_NM: 'trsLspNm',
  ETC1: 'etc1',
  ETC2: 'etc2',
  ETC3: 'etc3',
  ETC4: 'etc4',
  ETC5: 'etc5',
} as const satisfies Record<string, keyof ReturnManagementRow>

const EDITABLE_ENTRIES = Object.entries(EDITABLE_FIELDS) as [keyof typeof EDITABLE_FIELDS, keyof ReturnManagementRow][]

/** 표 id(생성본 columnLayout 키) → gridCd — 화면관리 그리드 설정과 개인 컬럼 배치 저장이 같은 값을 쓴다(docs/conventions/grid-code.md, LOGISPRD-17253) */
const GRID_CDS = { rgr04Table: 'RGR15_HEAD' } as const

/**
 * [저장] 필수 검사 후보 — 저장 본문(toWriteBody)에 실리는 편집 칸 전부. 제목은 생성본 COLUMNS 와 같게 적는다(생성본 컴포넌트 안 상수라 가져올 수 없다).
 *  기본 별표는 생성본 COLUMNS 의 required 칸(GENERATED_REQUIRED)이다 — 설정이 그 칸을 다루지 않으면 표 별표가 남으므로 검사도 같이 막는다
 *  (EST04·GR07 과 같은 규칙, LOGISPRD-17250·17251). 필수 여부는 화면관리 그리드 설정이 덮는다(LOGISPRD-17253).
 *  1.0 RGR04 는 FilterBar.onSave → CommonUtil.onSaveData 가 선택 행의 required 칸을 막았다(FilterBar.js:649).
 */
const REQUIRED_TITLES: Record<keyof typeof EDITABLE_FIELDS, string> = {
  ORD_NO: '반품번호',
  FLAG: '검색',
  PLN_DATE: '반품일자',
  TRS_INVOICE_NO: '반송번호',
  ORD_OPT_NO: '입고번호',
  RL_CHANNEL: '반품처',
  REF_NO2: '원일련번호',
  REF_NO3: '송장',
  BARCODE: 'SKU',
  PROC_NM: '비고',
  REF_NO1: '거래처주문번호',
  GDS_CD: '상품코드',
  GDS_NM: '상품명',
  GDS_OPT: '사이즈',
  ORD_QTY: '수량',
  TRS_NM: '수하인',
  TRS_PHONE: '휴대폰',
  TRS_NUMBER: '전화',
  TRS_ADDR: '주소',
  SAL_CHANNEL: '판매업체',
  BRD_NM: '브랜드명',
  TRS_LSP_NM: '반품택배',
  ETC1: '반품계좌',
  ETC2: '반송금액',
  ETC3: '착불금액',
  ETC4: '선/착불',
  ETC5: '정불구분',
}
const GENERATED_REQUIRED: ReadonlySet<string> = new Set(['PLN_DATE', 'TRS_INVOICE_NO', 'REF_NO2', 'REF_NO3', 'ORD_QTY', 'TRS_NM', 'TRS_ADDR'])
export const REQUIRED_CANDIDATES: core.CoreColumnType[] = EDITABLE_ENTRIES.map(([dataIndex]) => ({
  dataIndex,
  title: REQUIRED_TITLES[dataIndex],
  required: GENERATED_REQUIRED.has(dataIndex),
}))

/** BE 가 늘 막는 칸 + 설정이 필수로 켠 편집 칸 — 행 키는 EDITABLE_FIELDS 로 바꾼다. 같은 칸은 한 번만 */
export const toRequiredFields = (columns: core.CoreColumnType[]): RequiredField[] => {
  const fromSetting = EDITABLE_ENTRIES.flatMap(([, key], i) => (columns[i]?.required ? [{ key, title: String(columns[i].title) }] : []))
  return [...BE_REQUIRED_FIELDS, ...fromSetting.filter((f) => !BE_REQUIRED_FIELDS.some((b) => b.key === f.key))]
}

const asText = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '')
const asList = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .map(asText)
    .map((v) => v.trim())
    .filter(Boolean)
/** 유형 옵션 빈 목록 — 옵션 슬롯은 렌더마다 불리므로 참조를 고정한다. */
const NO_OPTIONS: Option[] = []

/** 응답의 `yyyyMMddHHmmss` 를 화면 표기로 끊는다(옛 columnDefinitions `toDateTimeText` 와 같다). */
const toDateTimeText = (value: unknown): string => {
  const s = asText(value)
  if (s.length !== 14) return s
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}:${s.slice(12, 14)}`
}

/** 편집 행 → 뷰 행. key = 행 rowKey(BE id 기반 `r-<id>` · 신규 `new-<seq>`) — 인덱스 key 면 재조회 뒤 체크가 다른 행에 붙는다(규칙 2). */
export const toViewRow = (row: ReturnManagementRow, coMingleEnabled = false): Row => ({
  key: row.rowKey,
  ...Object.fromEntries(EDITABLE_ENTRIES.map(([viewKey, field]) => [viewKey, row[field] ?? ''])),
  SPR_NM: (coMingleEnabled ? row.shipperName : null) ?? row.sprNm ?? '',
  SKU_ID: row.skuId ?? '',
  VIDEO: row.videoCount,
  ATCH_FILE: row.atchFileCount,
  INS_DATE: toDateTimeText(row.insDate),
  INS_BY: row.insBy ?? '',
  UPD_DATE: toDateTimeText(row.updDate),
  UPD_BY: row.updBy ?? '',
  // TODO(api): 반품/교환 — 조회 응답(ReturnManagementDto)에 없다(옛 화면 D8 과 같이 빈 칸).
  RTN_EXC_DIV: '',
  // DS 표 계약 — 신규 행은 필수 빈 칸을 처음부터 빨갛게 표기한다(데모 tableAppendRow 와 같다).
  ...(row.isNew ? { __new: true } : {}),
})

/** 뷰 행(편집 셀 반영본) → 편집 행. 편집 셀 키만 되돌린다 — 나머지는 원래 행 값을 지킨다. */
export const fromViewRow = (view: Row, base: ReturnManagementRow): ReturnManagementRow => {
  const next: Record<string, unknown> = { ...base }
  EDITABLE_ENTRIES.forEach(([viewKey, field]) => {
    if (viewKey in view) next[field] = view[viewKey]
  })
  return next as ReturnManagementRow
}

const rowKeyOf = (row: Row): string => asText(row.key)

// ── 조회영역 값(뷰 filterKey) ↔ 옛 폼 값 ─────────────────────────────────────────

/** 뷰 조회영역 값 → 옛 폼 값 모양. 뷰 화주 칸은 유형·화주명 모두 단일 셀렉트('전체' = 빈 값)다. */
export const toFormValues = (filters: Record<string, unknown>): ReturnManagementFormValues => {
  const [from, to] = Array.isArray(filters.PLN_DATE) ? (filters.PLN_DATE as unknown[]).map(asText) : []
  return {
    logisticsServiceTypes: asList(filters.service_type),
    shipperCodes: asList(filters.shipper_name),
    plnDateRange: from && to ? [dayjs(from), dayjs(to)] : null,
    insBy: asText(filters.INS_BY).trim(),
    rlChannel: asText(filters.RL_CHANNEL),
  }
}

/** 옛 폼 값(기본값·URL 복원값) → 뷰 조회영역 값. 없는 날짜는 싣지 않는다(기본값을 undefined 로 덮지 않게). */
export const toFilterValues = (values: ReturnManagementFormValues): Record<string, unknown> => {
  const [from, to] = values.plnDateRange ?? [null, null]
  return {
    service_type: (values.logisticsServiceTypes ?? []).slice(0, 1),
    shipper_name: asList(values.shipperCodes).slice(0, 1),
    ...(from && to ? { PLN_DATE: [from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')] } : {}),
    INS_BY: values.insBy ?? '',
    RL_CHANNEL: values.rlChannel ?? '',
  }
}

// ── 주문검색(송장 REF_NO3 헬퍼) ────────────────────────────────────────────────

/**
 * 모달 [조회] 적용 조건 → 주문검색 요청. 출고일자는 BE 필수인데 데모 모달에 날짜 칸이 없다 —
 * 옛 모달 기본값(오늘 기준 30일)을 그대로 보낸다. TODO(dev): 기간을 바꿔야 하면 데모에 날짜 칸이 필요하다(PD 결정).
 */
export const toOrderSearchReq = (filters: Record<string, unknown>, shipper: string): OrderSearchReq => {
  const one = (key: string) => asText(filters[key]).trim() || undefined
  const many = (key: string) => {
    const v = one(key)
    return v ? [v] : undefined
  }
  return {
    actDateFrom: dayjs().subtract(ORDER_SEARCH_DAYS, 'day').format(ORDER_SEARCH_DATE_FORMAT),
    actDateTo: dayjs().format(ORDER_SEARCH_DATE_FORMAT),
    shipper: shipper || undefined,
    barcode: many('BARCODE'),
    refNo1: many('REF_NO1'),
    trsInvoiceNo: one('TRS_INVOICE_NO'),
    ordNm: one('ORD_NO'),
    trsNm: one('TRS_NM'),
    trsPhone: one('TRS_PHONE'),
    trsAddr: one('TRS_ADDR'),
  }
}

const toDashDate = (compact: string | null): string =>
  compact?.length === 8 ? `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}` : compact ?? ''

/** 주문검색 응답 → 모달 표 행(COLUMNS2 키). 원본은 `__item` 에 실어 [선택] 슬롯이 그대로 쓴다. */
export const toOrderViewRow = (item: OrderSearchItemRes, index: number): Row => ({
  key: `${index}-${item.ordNm ?? ''}-${item.refNo1 ?? ''}-${item.barcode ?? ''}`,
  PLN_DATE: toDashDate(item.actDate),
  ORD_NO: item.ordNm ?? '',
  TRS_NM: item.trsNm ?? '',
  TRS_PHONE: item.trsPhone ?? item.trsMobile ?? '',
  TRS_ADDR: item.trsAddr ?? '',
  BARCODE: item.barcode ?? '',
  ORD_QTY: item.actQty ?? '',
  REF_NO1: item.refNo1 ?? '',
  SAL_CHANNEL: item.salChannel ?? '',
  // 송장 칸 = 고르면 채워지는 값(1.0 HelperEditor 결과 = refNo3).
  REF_NO3: item.refNo3 ?? '',
  __item: item,
})

/**
 * 주문검색 [선택] 패치(뷰 컬럼 키). FLAG 가 'Y' 인 행만 수량·수하인·연락처·주소·판매업체까지 채운다(1.0 HelperEditor.js:133, 피드백 13) —
 * 그 외는 송장만. 셀이 편집 가능해진 뒤로는 화면에서 방금 고른 FLAG 가 기준이다(LOGISPRD-12604).
 */
export const toOrderPickPatch = (item: OrderSearchItemRes, target: Row | undefined): Record<string, unknown> => {
  const patch: Record<string, unknown> = { REF_NO3: item.refNo3 ?? '' }
  if (asText(target?.FLAG) === 'Y') {
    patch.ORD_QTY = item.ordQty ?? item.actQty ?? 0
    patch.TRS_NM = item.trsNm ?? ''
    patch.TRS_PHONE = item.trsPhone ?? ''
    patch.TRS_ADDR = item.trsAddr ?? ''
    patch.SAL_CHANNEL = item.salChannel ?? ''
  }
  return patch
}

// ── 기타 ─────────────────────────────────────────────────────────────────────

/** 실패 응답에서 서버 메시지를 꺼낸다. 2.0 StandardResponse 는 `meta.message` 에 싣는다(옛 화면과 같다). */
const extractErrorMessage = (error: unknown, fallback: string): string => {
  const resp = (error as { response?: { data?: { meta?: { message?: string } } } })?.response
  return resp?.data?.meta?.message || fallback
}

/** `Content-Disposition` 에서 파일명을 꺼낸다(옛 화면과 같다). 없으면 zip 폴백. */
const getFileNameFromDisposition = (disposition: string): string => {
  const utf8Match = /filename\*=UTF-8''([\w%\-.]+)(?:; ?|$)/i.exec(disposition)
  if (utf8Match) return decodeURIComponent(utf8Match[1])
  const asciiMatch = /filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i.exec(disposition)
  return asciiMatch?.[2] || ''
}

/** Safari 는 다운로드를 비동기로 처리해 동기 revoke 면 blob URL 이 먼저 풀린다 — 링크를 잠깐 붙였다 떼고 revoke 를 늦춘다(옛 화면과 같다). */
const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), BLOB_REVOKE_DELAY_MS)
}

function Rgr04V2({ coMingleEnabled }: CoMingleReturnProps) {
  // 계정은 마운트 시점 한 번 — 화주 전환은 reload 를 동반해 새 마운트를 만든다. 기준은 현재 화주(shipperCode, 전환 반영)다(rgr10·gr02 규약).
  //  비전환 상태에서는 SPR_NM 과 같은 값이다 — 옛 화면이 기본값으로 쓰던 값과 같다.
  //  재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고르고 기본값은 MUSINSA 다. 고른 화주 그대로 조회한다(LOGISPRD-15725, `withCoMingle`).
  const accountRef = useRef<{ ownShipperCode: string; isFreeShipper: boolean; scope: ShipperScope }>()
  if (!accountRef.current) {
    const scope = withCoMingle(shipperScopeOf(AuthStorage.user.get()), coMingleEnabled) // 잠금 판정은 2.0 공용 규칙 — FREE_SHIPPERS 4종 외는 자기 화주로 고정(LOGISPRD-14652)
    accountRef.current = { ownShipperCode: scope.shipperCode, isFreeShipper: !scope.locked, scope }
  }
  const { ownShipperCode: accountShipperCode, isFreeShipper, scope } = accountRef.current
  /** 조회조건 기본 화주 — 재고통합 대상 계정은 MUSINSA, 그 밖은 자기 화주(LOGISPRD-15718) */
  const defaultShipperCode = defaultShipper(scope).shipperCode
  /** 저장·삭제·확정·첨부·주문검색의 화주 판정용 — 재고통합 ON 이면 세 화주를 MUSINSA 로 접는다(조회 경로 밖이라 그대로 둔다, LOGISPRD-15725) */
  const ownShipperCode = toReturnShipper(accountShipperCode, coMingleEnabled)

  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<ReturnManagementSearchQuery>()
  // 마운트 시 한 번만 — 이후 조회로 URL 이 바뀌어도 화면 상태를 덮지 않는다(D16). 화주 고정 계정은 복원값도 자기 화주로(규칙 5).
  const [restored] = useState(() => {
    const r = fromSearchQuery(parsedQs as Partial<ReturnManagementSearchQuery>, defaultShipperCode)
    // 화주유형은 요청에 싣지 않는 표시값이다(toReturnManagementReq). 잠금 계정은 URL 의 유형을 버리고 화주 목록이 온 뒤 filterFallback 이 고른 화주의 유형을 채운다(LOGISPRD-14949).
    //  잠금 계정의 화주는 고를 수 있는 화주면 그대로, 아니면 기본 화주로 덮는다(`scopedShipperCode`).
    return isFreeShipper
      ? r
      : { ...r, values: { ...r.values, shipperCodes: scopedShipperCode(scope, asList(r.values.shipperCodes)[0]), logisticsServiceTypes: [] } }
  })

  // ── 옵션 ──
  const { data: shippers = [] } = useGetShipperOptions([])
  const visibleShippers = useMemo(
    () => (isFreeShipper ? shippers : shippers.filter((s) => selectableShippers(scope).some((c) => c.shipperCode === s.shipperCode))),
    [shippers, isFreeShipper, scope],
  )
  /** 잠금 계정이 고른 화주(고를 수 없는 값이면 기본 화주)의 유형 — 화주 목록의 logisticsServiceType. 목록이 오기 전엔 모른다(filterFallback 이 늦게 채운다). (LOGISPRD-14949) */
  const lockedServiceTypeOf = (code: unknown): string | undefined => {
    if (isFreeShipper) return undefined
    const scoped = scopedShipperCode(scope, asList(code)[0])
    return visibleShippers.find((s) => s.shipperCode === scoped)?.logisticsServiceType || undefined
  }
  const serviceTypeOptions = useMemo<Option[]>(() => {
    const seen = new Set<string>()
    return visibleShippers.reduce<Option[]>((acc, s) => {
      if (!s.logisticsServiceType || seen.has(s.logisticsServiceType)) return acc
      seen.add(s.logisticsServiceType)
      acc.push({ value: s.logisticsServiceType, label: s.logisticsServiceType })
      return acc
    }, [])
  }, [visibleShippers])
  /** 잠금 계정의 유형 옵션 — 고른 화주의 유형 하나. 옵션 슬롯은 매 렌더 불리므로 유형별로 같은 배열 참조를 돌려준다 */
  const lockedTypeOptions = useMemo(() => new Map(serviceTypeOptions.map((o): [string, Option[]] => [String(o.value), [o]])), [serviceTypeOptions])
  /** 화주유형별 화주명 옵션(value = 화주코드, 라벨에 코드 병기) — 같은 유형이면 같은 배열 참조를 돌려준다(옵션 슬롯은 매 렌더 불린다). */
  const nameOptionsByType = useMemo(() => {
    const cache = new Map<string, Option[]>()
    return (types: string[]): Option[] => {
      const key = types.join(',')
      const hit = cache.get(key)
      if (hit) return hit
      const built = buildShipperNameOptions(visibleShippers, { valueKey: 'shipperCode', serviceTypes: types })
      cache.set(key, built)
      return built
    }
  }, [visibleShippers])

  // 반품처(RL_CHANNEL) — 조회영역 셀렉트·편집 셀 두 곳이 같은 목록을 쓴다. 'ALL' 행은 뺀다(조회영역은 뷰가 '전체' 를 따로 붙인다, LOGISPRD-12385).
  const { data: combos } = useGetCombos(['RL_CHANNEL'], { errorToast: true })
  const rlChannelOptions = useMemo(() => toSelectOptions(excludeAllOption(combos?.RL_CHANNEL)), [combos])
  // 다운로드 사유코드 — 공용 콤보(DOWNLOAD_CD, D7). 데모 목데이터가 나가면 안 된다(규칙 7).
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo<Option[]>(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [exportCombo],
  )

  // ── 조회 ──
  const [searchCondition, setSearchCondition] = useState<GetReturnManagementReq>(() =>
    restored.hasQuery ? toReturnManagementReq(restored.values) : {},
  )
  const [hasSearched, setHasSearched] = useState(restored.hasQuery)
  // 마지막 조회에 쓴 조회영역 값 — 목록 검색어 저장·행 추가 기본값(반품처)이 함께 쓴다.
  const lastSearchedRef = useRef<ReturnManagementFormValues>(restored.values)
  // 저장·삭제·확정·첨부의 대상 화주 = 이미 조회에 쓴 화주. 화면이 화주 일치를 검증하지 않고 서버 403 을 그대로 보인다(피드백 12, D14).
  const saveShipper = searchCondition.shipper === ownShipperCode ? accountShipperCode : searchCondition.shipper ?? ''

  const { data, isFetching, refetch } = useGetReturnManagements(searchCondition, hasSearched)
  const {
    rows,
    setRowsFromServer,
    overlayRows,
    onRowsChange,
    addRow,
    addRowsFromImport,
    deleteRows,
    getPendingRows,
    findMissingRequired,
    buildSavePayload,
    buildDeletePayload,
    applyMediaCounts,
  } = useReturnManagementRows()
  // 화면관리 그리드 설정(LOGISPRD-17253) — 툴바·별표·[일괄 입력]·[저장] 필수 검사가 RGR15_HEAD 를 따른다. 모달 안 표는 메뉴 단위 값
  const permission = usePermissionSlot('rgr15', GRID_CDS)
  const rowsRef = useRef(rows)
  rowsRef.current = rows

  // 첨부·동영상 개수(4.5) — 조회 응답(검색 화주)의 fileId 를 모아 한 번에 받는다. extraFileIds = 첫 업로드로 방금 생긴 fileId.
  const [extraFileIds, setExtraFileIds] = useState<string[]>([])
  const searchFileIds = useMemo(() => {
    const base = (data?.data?.items ?? []).map((item) => item.fileId).filter(Boolean) as string[]
    return extraFileIds.length ? [...new Set([...base, ...extraFileIds])] : base
  }, [data, extraFileIds])
  const mediaGroups = useMemo(() => {
    if (!coMingleEnabled) return undefined
    const groups = new Map<string, Set<string>>()
    ;(data?.data?.items ?? []).forEach((item) => {
      if (!item.fileId) return
      const shipper = item.sprNm || saveShipper
      const ids = groups.get(shipper) ?? new Set<string>()
      ids.add(item.fileId)
      groups.set(shipper, ids)
    })
    extraFileIds.forEach((fileId) => {
      const shipper = rowsRef.current.find((row) => row.fileId === fileId)?.sprNm || saveShipper
      const ids = groups.get(shipper) ?? new Set<string>()
      ids.add(fileId)
      groups.set(shipper, ids)
    })
    return [...groups].map(([shipper, fileIds]) => ({ shipper, fileIds: [...fileIds] }))
  }, [coMingleEnabled, data, extraFileIds, saveShipper])
  const { filesByFileId, counts: mediaCounts, refetch: refetchMediaFiles } = useRowMediaFiles('RGR15', saveShipper, searchFileIds, mediaGroups)
  const mediaCountsRef = useRef(mediaCounts)
  mediaCountsRef.current = mediaCounts
  useEffect(() => {
    applyMediaCounts(mediaCounts)
  }, [mediaCounts, applyMediaCounts])

  /**
   * 조회 결과를 표에 싣는다 — 응답이 올 때마다 효과로 덮지 않고 요청한 쪽이 직접 싣는다.
   * 저장·삭제·확정 뒤 재조회가 고르지 않은 행의 편집·미저장 신규 행을 지우지 않게 `pending` 을 다시 얹는다(overlayRows).
   * 같은 조건이면 react-query 가 같은 data 참조를 돌려줘 효과로는 안 돈다 — [조회]가 미저장 행을 걷으려면 직접 실어야 한다.
   * 재조회는 첨부·동영상 개수를 0 으로 되돌리므로(toEditableRows) 받아 둔 개수를 다시 얹는다.
   */
  const load = useCallback(
    async (pending: ReturnManagementRow[] = []) => {
      const { data: result, error } = await refetch({ cancelRefetch: false })
      // 실패 안내는 전역 조회 실패 알림(queryClient — 2.0 화면이면 새 알럿, 서버 문구 그대로) 한 번이다. 여기서도 띄우면 알럿이 두 장 겹친다
      //  (2026-10-02 QA). 전역 알림을 끄지 않는 건 URL 복원 진입 때 자동 조회(enabled=hasSearched)의 실패도 알려야 해서다.
      if (error) return null
      const items = result?.data?.items ?? []
      setRowsFromServer(items)
      overlayRows(pending)
      applyMediaCounts(mediaCountsRef.current)
      // 첫 업로드로 생긴 fileId 는 이제 조회 응답에 실려 온다 — 따로 들고 있을 이유가 없다(쌓이지 않게, #845 봇 리뷰).
      setExtraFileIds([])
      return items
    },
    [refetch, setRowsFromServer, overlayRows, applyMediaCounts],
  )

  // URL 복원 조건이 있으면 진입 때 한 번 조회한다(옛 화면: 복원된 조건이 있을 때만 마운트 조회).
  const bootedRef = useRef(false)
  useEffect(() => {
    if (bootedRef.current || !restored.hasQuery) return
    bootedRef.current = true
    load().catch(() => undefined)
  }, [restored.hasQuery, load])

  /** 조회 상태를 메뉴 세션·URL 에 남긴다. reload() 를 빼면 탭 스트립이 옛 조건을 되살린다(D16). 같은 값이면 다시 쓰지 않는다. */
  const persistedRef = useRef(
    restored.hasQuery
      ? JSON.stringify(toSearchQuery({ values: restored.values, page: 1, pageSize: DEFAULT_PAGE_SIZE, keyword: restored.keyword }))
      : '',
  )
  const persist = useCallback(
    (values: ReturnManagementFormValues, keyword: string) => {
      const query = toSearchQuery({ values, page: 1, pageSize: DEFAULT_PAGE_SIZE, keyword })
      const serialized = JSON.stringify(query)
      if (serialized === persistedRef.current) return
      persistedRef.current = serialized
      MenuStorage.menuOnSession.setSearch(query)
      menuSession.reload()
      replaceQuery(query)
    },
    [menuSession, replaceQuery],
  )

  // 화주유형을 바꿔도 고른 화주명은 칸에 남는다(뷰는 값을 옵션과 대조하지 않는다 — 옛 훅 useShipperNameFilterOptions 의 '유형만 바뀌면 화주명 비우기' 가 없다).
  //  그 조합은 늘 0건이라 조회 전에 막는다(gr02 선례). 단 **유형만 바뀐 경우**에만 — 기본값(M1P + 자기 화주)은 MWP·MFS 화주도 그대로 조회돼야 한다(옛 화면과 같다).
  const shipperObservedRef = useRef<{ types: string; name: string } | null>(null)
  const staleShipperNameRef = useRef<string | null>(null)

  const saveMutation = useSaveReturnManagements()
  // [저장] 진행 중 잠금 — isPending 은 다음 렌더에야 참이라 같은 틱 연타를 못 막는다(LOGISPRD-14894)
  const savingRef = useRef(false)
  const confirmMutation = useConfirmReturnManagements()
  const [isDownloading, setIsDownloading] = useState(false)

  // ── 첨부·동영상(옛 MediaModal) ──
  const [mediaTarget, setMediaTarget] = useState<{ kind: 'video' | 'attach'; row: ReturnManagementRow } | null>(null)
  const mediaTargetRef = useRef(mediaTarget)
  mediaTargetRef.current = mediaTarget
  const openMedia = useCallback(
    (kind: 'video' | 'attach', target: CellTarget): boolean => {
      const row = rowsRef.current.find((r) => r.rowKey === target.rowKey)
      if (!row) return false
      // 미저장 행은 id 가 없어 첨부를 붙일 수 없다(피드백 7).
      if (row.isNew) {
        notice.invalid(MESSAGES.attachRequiresSave)
        return false
      }
      setMediaTarget({ kind, row })
      return false // 뷰 미디어 모달은 열지 않는다 — 아래 MediaModal 이 연다
    },
    [],
  )
  /** 첫 업로드면 서버가 만든 fileId 를 그 행에 심고 목록 조회 대상에 넣는다 — 안 그러면 다음 조회 전까지 방금 올린 파일이 안 보인다(옛 화면과 같다). */
  const handleMediaUploaded = useCallback(
    (newFileId?: string) => {
      if (newFileId) {
        setExtraFileIds((prev) => (prev.includes(newFileId) ? prev : [...prev, newFileId]))
        const targetRowKey = mediaTargetRef.current?.row.rowKey
        if (targetRowKey) {
          onRowsChange(rowsRef.current.map((r) => (r.rowKey === targetRowKey ? { ...r, fileId: newFileId } : r)))
          setMediaTarget((prev) => (prev ? { ...prev, row: { ...prev.row, fileId: newFileId } } : prev))
        }
      }
      refetchMediaFiles().catch(() => undefined) // 개수 표시 실패는 작업을 막지 않는다(훅이 이미 삼킨다)
    },
    [onRowsChange, refetchMediaFiles], // 대상 행은 ref 로 읽는다 — 모달 열림·닫힘마다 콜백을 새로 만들지 않게(#845 봇 리뷰)
  )

  // ── 주문검색 모달 열림(후보 조회 게이트, 규칙 13·14) ──
  const [orderSearchOpen, setOrderSearchOpen] = useState(false)

  const viewRows = useMemo(() => rows.map((row) => toViewRow(row, coMingleEnabled)), [rows, coMingleEnabled])
  /** 선택 행 키 — 행 값은 편집 반영본(rows)에서 되찾는다(뷰가 넘긴 행은 표시용 사본이다). */
  const selectedKeys = (ctx: ActionCtx): Set<string> => new Set(ctx.selected.map(rowKeyOf))

  /** 새 행 기본값 — 화주코드는 조회에 쓴 화주(편집 불가 필수 칸이라 비우면 채울 방법이 없다), 반품처는 조회에 쓴 값(LLD 5장 4단계). */
  const newRowDefaults = (): Partial<ReturnManagementRow> => ({
    ...(saveShipper ? { sprNm: saveShipper } : {}),
    ...(lastSearchedRef.current.rlChannel ? { rlChannel: lastSearchedRef.current.rlChannel } : {}),
  })

  // ── 뷰 배선(Wiring) — 매 렌더 새로 만든다(문서 51) ──
  const wiring: Wiring = {
    permission,
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { rgr04Table: coreTableLayoutSlot(GRID_CDS.rgr04Table) },
    rows: {
      // 전건이 한 번에 온다(서버 페이징 없음, 3.1) — { rows, loading } 으로 로딩만 알린다(total 이 없으면 뷰가 클라이언트 페이징).
      ROWS: () => ({ rows: viewRows, loading: isFetching }),
      /**
       * 주문검색 후보 — 모달이 열려 있고 모달 [조회]를 눌렀을 때만(q.filters.__applied) 조회한다(규칙 14). 통합 목록의 기존 행은 원본 화주로 찾는다.
       * ⚠️ 슬롯 안 훅: 생성본이 이 슬롯을 컴포넌트 본문 첫머리에서 조건 없이 매 렌더 같은 순서로 부른다(문서 51 계약, ma02 선례).
       *   재핸드오프로 생성본이 바뀌면 호출부가 여전히 무조건 호출인지 먼저 확인할 것.
       */
      ROWS2: (_demo, q) => {
        const filters = q.filters ?? {}
        const applied = orderSearchOpen && filters.__applied === true
        const rowShipper = coMingleEnabled ? rowsRef.current.find((row) => row.rowKey === q.target?.rowKey)?.sprNm : undefined
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { data: found, isFetching: isOrderFetching } = useSearchReturnOrders(toOrderSearchReq(filters, rowShipper || saveShipper), applied)
        const items = applied ? found?.data?.items ?? [] : []
        return { rows: items.map(toOrderViewRow), loading: applied && isOrderFetching }
      },
    },
    actions: {
      /** [조회] — 화주·반품일자 필수(피드백 11). 조회를 안 했으면 false(뷰가 페이저·선택·검색칸을 되돌리지 않는다). */
      search: async (filters) => {
        const values = toFormValues(filters)
        if (!isFreeShipper) {
          // 화주 잠금 — 어떤 경로로 와도 고를 수 있는 화주, 아니면 기본 화주(규칙 5). 유형은 고른 화주의 것(LOGISPRD-15725)
          values.shipperCodes = [scopedShipperCode(scope, asList(values.shipperCodes)[0])]
          const lockedType = canSelectShipper(scope) ? lockedServiceTypeOf(values.shipperCodes) : undefined
          if (lockedType) values.logisticsServiceTypes = [lockedType]
        }
        const name = asList(values.shipperCodes)[0]
        const types = values.logisticsServiceTypes ?? []
        if (name && types.length && staleShipperNameRef.current === name) {
          const allowed = nameOptionsByType(types).map((o) => o.value)
          if (allowed.length > 0 && !allowed.includes(name)) {
            notice.invalid(MESSAGES.shipperNameNotInType)
            return false
          }
        }
        const condition = toReturnManagementReq(values)
        if (!hasRequiredFilter(condition)) {
          notice.invalid(MESSAGES.requiredFilter)
          return false
        }
        lastSearchedRef.current = values
        persist(values, '') // 조회하면 목록 검색어는 비운다(뷰도 비운다)
        flushSync(() => {
          setSearchCondition(condition)
          setHasSearched(true)
        })
        setExtraFileIds([])
        const items = await load()
        if (items?.length === 0) notice.invalid(MESSAGES.searchNoRows)
      },

      /**
       * 편집 셀 — 표가 돌려준 행(보이는 행)을 key 로 찾아 편집 셀만 되돌린다. 바코드가 바뀐 행의 SKUID 비우기는 옛 훅이 한다.
       * ⚠️ DS 표(0.8.2)는 이 콜백을 **자기 setState 업데이터 안에서** 부른다(table/index.tsx patchRow) — 곧바로 상태를 바꾸면 렌더 중 갱신이 되어
       *  업데이터가 다시 돌며 무한 갱신이 난다(이음매 테스트 실측 "Maximum update depth exceeded"). 한 틱 미루고, 값이 실제로 바뀐 때만 반영한다.
       *  표가 넘기는 행은 그 표의 편집 누적본이라 미룬 사이 여러 번 불려도 마지막 호출이 앞선 편집을 모두 담는다.
       */
      ROWS_rowsChange: (changed) => {
        const byKey = new Map(changed.map((r) => [rowKeyOf(r), r]))
        queueMicrotask(() => {
          let dirty = false
          const next = rowsRef.current.map((r) => {
            const view = byKey.get(r.rowKey)
            if (!view) return r
            const merged = fromViewRow(view, r)
            if (EDITABLE_ENTRIES.some(([, field]) => merged[field] !== r[field])) dirty = true
            return merged
          })
          if (dirty) onRowsChange(next)
        })
      },

      /** [+ 추가] — 빈 행을 맨 위에(데모 tableAppendRow · PD 확정 2026-09-30). 생성본은 이 버튼에 토스트를 내지 않는다. */
      rgr04AddRowButton: () => {
        addRow(newRowDefaults())
      },

      /**
       * [저장] — **고른 행** 중 신규·변경 행만 creates/updates 로 보낸다. 검증·API 실패는 안내 뒤 throw(선택 유지, 규칙 10).
       * 저장이 끝나면 재조회하되 고르지 않은 편집은 다시 얹는다.
       */
      rgr04SaveButton: async (ctx) => {
        // 받는 중이면 곧 덮일 행이다 — false 는 '성공'이라 throw 로 막는다(안내는 guardFetching 이 띄운다)
        if (!guardFetching(isFetching)) throw new Error(FETCH_ALL_MESSAGES.fetching)
        // 진행 중 연타는 안내 없이 던진다 — false 는 '성공'이라 생성본이 선택을 비운다(notice.tsx 규칙 10)
        if (savingRef.current) throw new Error('save in progress')
        savingRef.current = true
        try {
          const keys = selectedKeys(ctx)
          const missing = findMissingRequired(keys, toRequiredFields(permission.forTable('rgr04Table').columns(REQUIRED_CANDIDATES)))
          if (missing) throw invalidError(MESSAGES.saveMissingRequired(missing.title))
          // 저장한 그룹과 완료로 표시할 그룹이 같도록 한 번만 나눈다. await 사이에 rowsRef 가 바뀔 수 있다.
          const groups = groupReturnRowKeys(rowsRef.current, keys, saveShipper, coMingleEnabled)
          const payloads = [...groups]
            .map(([shipper, rowKeys]) => buildSavePayload(shipper, rowKeys))
            .filter((payload) => payload !== null)
          if (payloads.length === 0) {
            notice.invalid(MESSAGES.saveNoTarget)
            return false // 보낼 것이 없다 — 성공 토스트만 생략(뒷정리는 돈다)
          }
          const pending = getPendingRows(keys)
          const completed = new Set<string>()
          try {
            // 원본 화주별로 기존 단일 화주 저장 계약을 사용한다.
            // eslint-disable-next-line no-restricted-syntax
            for (const payload of payloads) {
              // eslint-disable-next-line no-await-in-loop
              await saveMutation.mutateAsync(payload)
              groups.get(payload.shipper)?.forEach((key) => completed.add(key))
            }
          } catch (e) {
            if (completed.size > 0) await load(getPendingRows(completed))
            throw failureError(extractErrorMessage(e, '저장에 실패했습니다.'))
          }
          // 저장은 끝났다 — 재조회 실패를 저장 실패로 오인시키지 않는다(load 가 따로 알린다).
          await load(pending)
          return MESSAGES.saveSuccess
        } finally {
          savingRef.current = false
        }
      },

      /**
       * [삭제] 확인 — 저장된 행은 deleteIds 만 담은 저장 요청을 **즉시** 보낸다(서버 성공 뒤에만 화면에서 뺀다, 규칙 11).
       * 미저장 행만 골랐으면 화면에서만 뺀다.
       */
      rgr04DeleteRowButton_ok: async (ctx) => {
        const keys = selectedKeys(ctx)
        if (keys.size === 0) return false
        const groups = groupReturnRowKeys(rowsRef.current, keys, saveShipper, coMingleEnabled)
        const payloads = [...groups]
          .map(([shipper, rowKeys]) => buildDeletePayload(shipper, rowKeys))
          .filter((payload) => payload !== null)
        if (payloads.length === 0) {
          deleteRows([...keys])
          return MESSAGES.deleteLocalOnly
        }
        const pending = getPendingRows(keys)
        const completed = new Set<string>()
        try {
          // eslint-disable-next-line no-restricted-syntax
          for (const payload of payloads) {
            // eslint-disable-next-line no-await-in-loop
            await saveMutation.mutateAsync(payload)
            groups.get(payload.shipper)?.forEach((key) => completed.add(key))
          }
        } catch (e) {
          if (completed.size > 0) {
            deleteRows([...completed])
            await load(getPendingRows(completed))
          }
          throw failureError(extractErrorMessage(e, '삭제에 실패했습니다.'))
        }
        deleteRows([...keys])
        await load(pending)
        return MESSAGES.deleteSaved
      },

      /** [확정] — 체크 선택과 무관하게 조회 화주의 미확정 전건이 대상이다(3.3). 건수를 토스트로. */
      rgr04HdrConfirm_ok: async () => {
        const pending = getPendingRows()
        let affected = 0
        let confirmedShippers = 0
        try {
          const shippersToConfirm = coMingleEnabled && rowsRef.current.length > 0
            ? [...new Set(rowsRef.current.map((row) => row.sprNm || saveShipper))]
            : [saveShipper]
          // eslint-disable-next-line no-restricted-syntax
          for (const shipperCode of shippersToConfirm) {
            // eslint-disable-next-line no-await-in-loop
            const result = await confirmMutation.mutateAsync({ shipperCode })
            affected += result.data.affectedRows
            confirmedShippers += 1
          }
        } catch (e) {
          // 앞 화주가 이미 확정됐으면 화면을 서버 상태로 맞춘다(저장·삭제와 같다).
          if (confirmedShippers > 0) {
            await load(pending)
            // 앞 화주 확정분은 이미 반영됐다. 전체 실패로 읽지 않게 건수를 함께 알린다.
            throw failureError(`${affected}건 확정 뒤 일부 화주 확정에 실패했습니다. ${extractErrorMessage(e, '')}`.trim())
          }
          throw failureError(extractErrorMessage(e, '확정에 실패했습니다.'))
        }
        await load(pending)
        return affected > 0 ? `${affected}건 ${MESSAGES.confirmSuccess}` : MESSAGES.confirmNone
      },

      /** 엑셀 다운로드 — 사유·비밀번호 검증(옛 모달 zod 스키마) 뒤 서버가 조회 조건으로 다시 뽑는다(화면 목록 검색과 무관, 3.4). */
      rgr04DownloadModal_ok: async (ctx) => {
        const parsed = downloadReasonSchema.safeParse({
          reasonCode: asText(ctx.form?.reason),
          reasonDetail: asText(ctx.form?.detail),
          password: asText(ctx.form?.password),
        })
        if (!parsed.success) throw invalidError(parsed.error.issues[0]?.message ?? '입력값을 확인해 주세요.')
        const reason = parsed.data
        setIsDownloading(true)
        try {
          const response = await downloadReturnManagements({
            ...searchCondition,
            reasonCode: reason.reasonCode,
            detailedReason: reason.reasonDetail,
            password: reason.password,
          })
          const filename = getFileNameFromDisposition(String(response.headers?.['content-disposition'] || '')) || `반품관리_${Date.now()}.zip`
          triggerBlobDownload(response.data, filename)
        } catch (e) {
          throw failureError(extractErrorMessage(e, '다운로드에 실패했습니다.'))
        } finally {
          setIsDownloading(false)
        }
      },

      /** 주문검색 [선택] — 고른 후보로 연 셀의 행을 패치한다(뷰가 cellPatch → onRowsChange → ROWS_rowsChange 로 되돌린다). */
      rgr04OrderSearchModal_ok: (ctx) => {
        // 원본은 toOrderViewRow 가 심은 __item — 뷰 Row 타입 밖이라 형식을 확인하고 꺼낸다(#845 봇 리뷰).
        const raw = ctx.picked?.[0]?.__item
        if (!raw || typeof raw !== 'object') throw invalidError('주문을 선택해 주세요.')
        return toOrderPickPatch(raw as OrderSearchItemRes, ctx.target?.row)
      },

      /** 엑셀 업로드 — 클라이언트가 파싱해 신규 행으로 넣는다(업로드 API 없음, D7 후단 — 감사로그만 남긴다). 화주코드만 화면 값으로 덮는다. */
      rgr04UploadModal_ok: async (ctx) => {
        const file = ctx.form?.file
        if (!(file instanceof File)) throw invalidError(MESSAGES.excelUploadNoFile)
        let parsed: Record<string, unknown>[] = []
        try {
          parsed = await parseReturnManagementExcel(file)
        } catch (e) {
          // 파서 문구는 대개 행을 짚는다("N행: …") — 놓치지 않게 알럿(헤더 불일치도 같은 자리로 온다).
          throw rowInvalidError(e instanceof Error ? e.message : '엑셀 파일을 읽을 수 없습니다.')
        }
        if (parsed.length === 0) throw invalidError(MESSAGES.excelUploadEmpty)
        const count = addRowsFromImport(parsed, saveShipper ? { sprNm: saveShipper } : {})
        callToAddImportLog({ menuCode: 'RGR15', apiName: 'RGR15_LIST_IMPORT', rowCount: count })
        noticeIfUploadExceedsPage(count)
        return `${count}행을 가져왔습니다. 검토 후 저장하세요.`
      },

      // 첨부·동영상은 cellOpens 가 옛 MediaModal 로 돌린다 — 뷰 미디어 모달은 열리지 않아 이 두 슬롯에 닿지 않는다(닿으면 모달 유지).
      //  서버 실패가 아니라 이 경로를 막는 안내라 검증(토스트)이다(#899 리뷰).
      rgr04VideoModal_ok: () => {
        throw invalidError(MESSAGES.mediaUnreachable)
      },
      rgr04AttachModal_ok: () => {
        throw invalidError(MESSAGES.mediaUnreachable)
      },
    },
    options: {
      // 잠금 계정은 고른 화주의 유형 하나(LOGISPRD-15725)
      RGR04SHIPPER_TYPE_OPTIONS: (_demo, filters) => {
        if (isFreeShipper) return serviceTypeOptions
        const type = lockedServiceTypeOf(filters?.shipper_name)
        return (type && lockedTypeOptions.get(type)) || NO_OPTIONS
      },
      // 유형-변경 추적(아래 두 ref)은 렌더 중 대입이지만 **같은 입력이면 결과가 같다**(직전 관측과 같으면 아무것도 바꾸지 않는다) —
      //  StrictMode 이중 렌더로 두 번 불려도 두 번째 호출은 prev === 현재라 stale 판정이 뒤집히지 않는다. 옵션 슬롯이 조회영역 값을
      //  받는 유일한 자리라 여기서 본다(onState 는 페이저·검색어만 준다).
      RGR04SHIPPER_NAME_OPTIONS: (_demo, filters) => {
        const types = asList(filters?.service_type)
        const name = asList(filters?.shipper_name)[0] ?? ''
        const prev = shipperObservedRef.current
        const typeKey = types.join(',')
        if (prev && prev.name !== name) staleShipperNameRef.current = null
        else if (prev && name && prev.types !== typeKey) staleShipperNameRef.current = name
        shipperObservedRef.current = { types: typeKey, name }
        // 잠금 계정은 유형으로 좁히지 않는다 — 유형 칸이 고른 화주를 따르므로 좁히면 다른 화주(MUSINSA)를 고를 수 없다(gr02V2 준용)
        return nameOptionsByType(isFreeShipper ? types : [])
      },
      RGR04CHANNEL_OPTIONS: () => rlChannelOptions,
      ROWS_RL_CHANNEL: () => rlChannelOptions,
      REASONRADIO_OPTIONS: () => reasonOptions,
    },
    // 기본값 — 화주유형 M1P(잠금 계정은 기본 화주의 유형) · 화주 = 기본 화주(재고통합 대상 계정은 MUSINSA) · 반품일자 오늘~오늘(D14). 뷰가 마운트 때 한 번 계산한다 — 자정을 넘겨 열어 둔 탭의 [초기화]는 전날로 돌아간다.
    filterDefaults: {
      rgr04FilterCard: (demo) => ({
        ...demo,
        ...toFilterValues({ ...buildDefaultFilters(defaultShipperCode), ...(isFreeShipper ? {} : { logisticsServiceTypes: asList(lockedServiceTypeOf(defaultShipperCode)) }) }),
      }),
    },
    filterInitial: { rgr04FilterCard: (defaults) => ({ ...defaults, ...toFilterValues(restored.values) }) },
    /** 잠금 계정의 화주유형은 화주 목록 응답 뒤에야 정해진다 — 비어 있을 때만 고른 화주의 유형으로 채운다(값이 있는 키는 덮지 않는다). */
    filterFallback: {
      rgr04FilterCard: (current) => {
        const type = lockedServiceTypeOf(current.shipper_name)
        return type && asList(current.service_type).length === 0 ? { service_type: [type] } : undefined
      },
    },
    /**
     * 화주 고정 계정은 화주유형·화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·fallback·search 가 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 화주명만 연다 — 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15725)
     */
    filterDisabled: isFreeShipper ? undefined : { service_type: true, shipper_name: !canSelectShipper(scope) },
    stateInitial: { listq: { 'table.q': restored.keyword } },
    /** 목록 검색어 확정 — 조회한 적이 있을 때만 URL/세션에 싣는다(옛 화면: 커밋될 때만). */
    onState: (s) => {
      if (!hasSearched) return
      persist(lastSearchedRef.current, s.listq['table.q'])
    },
    /** [초기화] — 저장된 조건·결과·선택까지 지운다(옛 handleReset). 조회는 하지 않는다(1.0 동치). */
    onReset: () => {
      staleShipperNameRef.current = null
      shipperObservedRef.current = null
      lastSearchedRef.current = buildDefaultFilters(defaultShipperCode)
      setSearchCondition({})
      setHasSearched(false)
      setRowsFromServer([])
      setExtraFileIds([])
      persistedRef.current = ''
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
    },
    opens: {
      // 확정 대상 화주는 조회에 쓴 화주다 — 조회 전에는 대상이 없다(옛 화면: 버튼 비활성).
      rgr04HdrConfirm: () => {
        if (!guardFetching(isFetching)) return false
        if (saveShipper) return true
        notice.invalid(MESSAGES.confirmNeedsSearch)
        return false
      },
      rgr04DeleteRowButton: () => guardFetching(isFetching),
      rgr04ExcelButton: () => {
        if (!guardFetching(isFetching)) return false
        if (rows.length > 0) return true
        notice.invalid(MESSAGES.excelNoRows)
        return false
      },
    },
    cellOpens: {
      rgr04VideoModal: (target) => openMedia('video', target),
      rgr04AttachModal: (target) => openMedia('attach', target),
    },
    onModal: (open) => setOrderSearchOpen(open.rgr04OrderSearchModal),
    // 요청 중이면 선택 게이트 버튼(저장·삭제)을 잠근다 — 중복 요청 방지(모달을 닫아서 막지 않는다, 규칙 11).
    useBusy: () => saveMutation.isPending || confirmMutation.isPending || isDownloading,
  }

  return (
    <>
      <ReturnManagePageView wiring={wiring} />
      <MediaModal
        menuCode="RGR15"
        open={Boolean(mediaTarget)}
        kind={mediaTarget?.kind ?? null}
        row={mediaTarget?.row ?? null}
        shipper={mediaTarget?.row.sprNm || saveShipper}
        filesByFileId={filesByFileId}
        onUploaded={handleMediaUploaded}
        onClose={() => setMediaTarget(null)}
      />
    </>
  )
}

export default withCoMingleReturns(Rgr04V2)
